import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { setSeo } from "../lib/seo";
import { api, apiError, track, trackBeacon } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PATHS = [
  { key: "A", title: "Prepare for the Role", count: "Five modules",
    body: "You want to enter Estate Management, but may not yet know how your experience translates into private service. Path A helps you recognise the skills you already hold, understand how they apply inside a private household, and present them with confidence and precision. You learn the language and expectations of private service, practise professional judgment in realistic household situations, and approach opportunities selectively and intentionally — rather than applying everywhere and hoping." },
  { key: "B", title: "Perform the Role", count: "Eleven modules",
    body: "You have the position. Path B teaches you how to enter and understand the household before you try to change it: observing carefully, asking respectful and useful questions, and gathering information without being intrusive. You learn to understand the Principal's expectations, introduce structure without unnecessarily disrupting how the family lives, hold professional boundaries, and communicate calmly under pressure. It carries you through the first thirty days and beyond — managing staff and vendors professionally, reporting progress without sounding critical, and moving from reacting to problems to working intentionally, building trust through consistency and follow-through." },
];

const LEARN = [
  ["Professional judgment", "Knowing when to ask, when to act and when to observe."],
  ["Observation before action", "Understanding how a household truly runs before adjusting anything."],
  ["Principal communication", "Speaking and writing clearly, briefly and at the right moment."],
  ["Professional boundaries", "Holding your role with grace — for yourself and for the family."],
  ["Discretion", "Knowing what belongs to the household, and keeping it there."],
  ["Calm communication under pressure", "Staying measured and precise when something goes wrong."],
  ["Trust-building behaviours", "Consistency, follow-through, and doing what you said you would."],
  ["Gathering information without intrusion", "Learning the house through respectful questions and quiet attention."],
  ["Introducing structure without disruption", "Building systems that serve the family's way of living."],
  ["First-days and first-30-days conduct", "How to carry yourself while trust, context and working rhythm are still being established."],
  ["Intentional decision-making", "Moving from reacting to problems to working with purpose."],
  ["Professional presence", "The bearing, language and reliability private service expects."],
];

const INCLUDED = [
  "Both guided pathways — Prepare for the Role and Perform the Role — taken one module at a time, at your own pace.",
  "Structured course teaching for every module: the situation you are in, what you accomplish, and what comes next.",
  "Practical exercises and realistic household scenarios, so you practise judgment before it is tested.",
  "Professional tools introduced at the point you need them — workbooks, a Course Guide and Course Navigator, an operating system, communication and reporting templates, and a 90-day plan — to help you put the teaching into practice.",
  "Every resource is classified — private, Principal-facing or team-facing — so nothing is shared before it should be.",
];
const INCLUDED_NOTE = "Resources are provided as editable documents and spreadsheets with structure and formulas preserved, with in-platform reading and a clean download whenever you prefer.";

export default function BecomingSales() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [enrolled, setEnrolled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSeo({ title: "Becoming an Estate Manager™ · Il Salotto Digitale",
      description: "The advanced Il Salotto Digitale course for entering private service with confidence and learning to operate with judgment, discretion and calm inside a household — two guided pathways, from career readiness to your first 90 days." });
    track("p2_view", "becoming-an-estate-manager");
  }, []);
  useEffect(() => {
    if (user) api.get("/my/courses").then((r) => setEnrolled(r.data.some((c) => c.id === "becoming-an-estate-manager"))).catch(() => {});
    else setEnrolled(false);
  }, [user]);

  const begin = async () => {
    track("p2_cta_click", "becoming-an-estate-manager");
    if (enrolled) { nav("/becoming"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { course_id: "becoming-an-estate-manager", origin_url: window.location.origin });
      trackBeacon("checkout_begin", "becoming-an-estate-manager");
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <div data-testid="becoming-sales" className="pt-20">
      {/* Hero — matches Product #1 two-column pattern */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <FadeIn><MetaLabel>Il Salotto Digitale · The Advanced Course</MetaLabel></FadeIn>
          <FadeIn delay={0.08}>
            <h1 className="mt-8 font-serif font-light text-ink text-4xl sm:text-5xl lg:text-[4.5rem] leading-[1.02] tracking-tight">Becoming an Estate Manager™</h1>
          </FadeIn>
          <FadeIn delay={0.14}>
            <p className="mt-8 font-serif italic text-xl lg:text-2xl text-ink-soft leading-snug" data-testid="p2-hero-subline">Enter private service with confidence — and learn to operate with judgment, discretion and calm once you are inside a household.</p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-6 text-lg font-light text-ink-soft leading-relaxed max-w-xl" data-testid="p2-hero-description">A guided, structured course through two professional pathways — one for those preparing to enter Estate Management, and one for those learning to understand a household before they change it: how to observe, how to communicate, how to hold professional boundaries, and how to build trust through consistency across the first ninety days.</p>
          </FadeIn>
          <FadeIn delay={0.26}>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <span className="font-serif text-3xl text-ink">$297</span>
              <button onClick={begin} disabled={busy} data-testid="sales-enrol-btn" className="group inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors disabled:opacity-60">
                {busy ? "Redirecting…" : enrolled ? "Go to the Course" : "Begin the Course"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
            <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-ink-soft">Self-paced · Lifetime access</p>
            <p className="mt-4 text-xs font-light text-ink-soft">
              <Link to="/terms" className="link-underline text-ink">Terms</Link>
              <span className="mx-2 text-ink-soft/40">·</span>
              <Link to="/refund-policy" className="link-underline text-ink">Refund Policy</Link>
            </p>
          </FadeIn>
        </div>
        <FadeIn delay={0.2} className="lg:col-span-5">
          <div className="overflow-hidden aspect-[4/5] bg-surface">
            <img src="/becoming-hero.webp" alt="Becoming an Estate Manager" data-testid="p2-hero-image" className="w-full h-full object-cover" />
          </div>
        </FadeIn>
      </section>

      {/* Next step after Product #1 */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-12 py-20 lg:py-24 text-center">
          <Reveal><p className="text-[11px] uppercase tracking-ultra text-accent">The Natural Next Step</p></Reveal>
          <Reveal delay={0.06}>
            <p className="mt-8 font-serif font-light text-2xl lg:text-3xl text-ink leading-snug max-w-3xl mx-auto">
              Where <span className="italic">How to Start as an Estate Manager</span> helps you understand the profession, <span className="italic">Becoming an Estate Manager™</span> prepares you to enter it — and to lead once you are inside a household.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base font-light text-ink-soft leading-relaxed max-w-2xl mx-auto">
              It is the advanced course in Il Salotto Digitale — a guided, structured progression through both pathways.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Purchase terms */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-12 py-16 lg:py-20">
          <Reveal><MetaLabel className="mb-8">Purchase Terms</MetaLabel></Reveal>
          <Reveal delay={0.06}>
            <p data-testid="sales-purchase-terms" className="text-base font-light text-ink-soft leading-relaxed max-w-3xl">
              One payment of $297 includes lifetime access to both pathways and all accompanying course resources — no subscription, no renewal fee, and access does not expire. This is a digital course with immediate access — all sales are final once access is granted. Secure payment via Stripe. By purchasing, you agree to our{" "}
              <Link to="/terms" className="link-underline text-ink">Terms</Link> and{" "}
              <Link to="/refund-policy" className="link-underline text-ink">Refund Policy</Link>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Two pathways */}
      <section className="border-t border-line mx-auto max-w-[1500px] px-6 lg:px-12 py-20 lg:py-28">
        <Reveal><MetaLabel className="mb-14">Two Pathways · One Course</MetaLabel></Reveal>
        <div className="grid lg:grid-cols-2 gap-10">
          {PATHS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.08}>
              <div className="border border-line p-8 lg:p-10 h-full">
                <p className="text-[11px] uppercase tracking-ultra text-accent">Path {p.key} · {p.count}</p>
                <h3 className="mt-4 font-serif font-light text-3xl lg:text-4xl text-ink tracking-tight">{p.title}</h3>
                <p className="mt-5 text-base font-light text-ink-soft leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-base font-light text-ink-soft max-w-3xl">
            You begin with the path that reflects where you are now. Your progress is kept separately for each path, and you can move between them without losing your place.
          </p>
        </Reveal>
      </section>

      {/* What you learn to do */}
      <section className="border-t border-line" data-testid="p2-learn-section">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="lg:col-span-4">
              <Reveal><MetaLabel>What You Learn to Do</MetaLabel></Reveal>
              <Reveal delay={0.06}>
                <p className="mt-8 font-serif font-light text-2xl lg:text-3xl text-ink leading-snug">The course is built around the way professionals conduct themselves inside a private household.</p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-base font-light text-ink-soft leading-relaxed">Emotional intelligence is the thread that runs through all of it — taught here as concrete professional behaviour, not as a separate subject.</p>
              </Reveal>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10 border-t border-line pt-10">
              {LEARN.map(([h, d], i) => (
                <Reveal key={h} delay={i * 0.03}>
                  <div data-testid={`p2-learn-${i + 1}`}>
                    <p className="font-serif text-xl text-ink leading-snug">{h}</p>
                    <p className="mt-2 text-sm font-light text-ink-soft leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Included with the course */}
      <section className="border-t border-line bg-surface" data-testid="p2-included-section">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-4">
            <Reveal><MetaLabel>Included with the Course</MetaLabel></Reveal>
            <Reveal delay={0.06}><p className="mt-8 text-base font-light text-ink-soft leading-relaxed">The resources support the teaching. Each is introduced at the point in the course where you need it.</p></Reveal>
          </div>
          <div className="lg:col-span-8">
            <div className="divide-y divide-line border-t border-line">
              {INCLUDED.map((r, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="py-6 flex items-start gap-4">
                    <Check size={18} className="mt-1 text-accent shrink-0" />
                    <p className="text-lg font-light text-ink leading-relaxed">{r}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}><p className="mt-6 text-xs font-light text-ink-soft leading-relaxed" data-testid="p2-included-note">{INCLUDED_NOTE}</p></Reveal>
          </div>
        </div>
      </section>

      {/* Close — credibility passage + CTA */}
      <section className="border-t border-line mx-auto max-w-[1100px] px-6 lg:px-12 py-20 lg:py-28">
        <div className="max-w-3xl mx-auto text-center" data-testid="p2-credibility">
          <Reveal><MetaLabel>A Note from Marella</MetaLabel></Reveal>
          <Reveal delay={0.06}><p className="mt-8 font-serif font-light text-3xl lg:text-4xl text-ink leading-snug">Private households are learned before they are managed.</p></Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base lg:text-lg font-light text-ink-soft leading-relaxed">Professional Estate Management is not walking into a new residence and changing everything you find. It begins with learning what to observe and what to leave alone, what to ask and when to communicate, and how to introduce structure so that the household runs more smoothly without ever feeling managed.</p>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 text-base lg:text-lg font-light text-ink-soft leading-relaxed">This course teaches that way of working, one module at a time — so that you build consistency first, and let trust follow from it.</p>
          </Reveal>
          <Reveal delay={0.18}>
            <button onClick={begin} disabled={busy} data-testid="sales-enrol-btn-bottom" className="group mt-12 inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors disabled:opacity-60">
              {busy ? "Redirecting…" : enrolled ? "Go to the Course" : "Begin the Course"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </button>
            <div className="mt-6">
              <Link to="/salotto-digitale" className="text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline">Return to Il Salotto Digitale</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
